"use client"

import React from "react"
import { useTimeFormat } from "@/hooks/use-time-format"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

interface ClockProps {
  size?: "small" | "large"
  className?: string
  timezone?: "local" | "utc"
  showLabel?: boolean
  showSeconds?: boolean
  showTimezone?: boolean
  showDate?: boolean
  hourFormat?: "12h" | "24h" // Optional override, otherwise uses user preference
  disableDialog?: boolean // If true, don't wrap in Dialog (for use in grouped dialogs)
}

const getTimezoneAbbreviation = (date: Date): string => {
  try {
    // Try to get the IANA timezone identifier
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone

    // Try short format first
    const shortFormatter = new Intl.DateTimeFormat("en", {
      timeZoneName: "short",
    })
    const shortParts = shortFormatter.formatToParts(date)
    const shortTZ = shortParts.find((part) => part.type === "timeZoneName")?.value || ""

    // Get timezone offset to help with detection
    const offset = -date.getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)

    // Check if we're in New Zealand timezone (UTC+12 or UTC+13)
    // This handles cases where browser returns GMT+13 instead of NZDT
    // The offset already tells us: UTC+13 = NZDT (DST), UTC+12 = NZST (standard)
    if (hours === 12 || hours === 13) {
      // Check if timezone ID suggests New Zealand, or if we got GMT+12/13 format
      const isNZTimezone = timeZoneId === "Pacific/Auckland" ||
        timeZoneId === "NZ" ||
        timeZoneId.includes("Auckland") ||
        timeZoneId.includes("Pacific")

      // If we got GMT+13 or GMT+12 format, assume it's NZ timezone
      const gotGMTFormat = shortTZ.startsWith("GMT+") || shortTZ.startsWith("GMT-")

      if (isNZTimezone || (gotGMTFormat && (hours === 12 || hours === 13))) {
        // Offset already accounts for DST:
        // UTC+13 = NZDT (Daylight Time)
        // UTC+12 = NZST (Standard Time)
        return hours === 13 ? "NZDT" : "NZST"
      }
    }

    // If we got a proper abbreviation (not GMT+XX), use it
    if (shortTZ && !shortTZ.startsWith("GMT")) {
      return shortTZ
    }

    // Try long format and extract abbreviation
    const longFormatter = new Intl.DateTimeFormat("en", {
      timeZoneName: "long",
    })
    const longParts = longFormatter.formatToParts(date)
    const longTZ = longParts.find((part) => part.type === "timeZoneName")?.value || ""

    // Fallback to short format if available
    return shortTZ || longTZ || ""
  } catch {
    // Fallback if Intl API fails
    const offset = -date.getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? "+" : "-"
    return `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
  }
}

export const Clock: React.FC<ClockProps> = ({
  size = "small",
  className,
  timezone = "local",
  showLabel = false,
  showSeconds = true,
  showTimezone = false,
  showDate = false,
  hourFormat,
  disableDialog = false
}) => {
  const [date, setDateTo] = React.useState<Date>(new Date())
  const { timeFormat: userTimeFormat } = useTimeFormat()

  // Use prop override if provided, otherwise use user preference
  const timeFormat = hourFormat || userTimeFormat

  React.useEffect(() => {
    const interval: NodeJS.Timeout = setInterval(() => {
      const update: Date = new Date()

      if (update.getSeconds() !== date.getSeconds()) {
        setDateTo(update)
      }
    }, 100)

    return () => {
      clearInterval(interval)
    }
  }, [date])

  const formatSegment = (segment: number): string => {
    return segment < 10 ? `0${segment}` : segment.toString()
  }

  const getHours = (hours24: number, format: "12h" | "24h"): number => {
    if (format === "24h") {
      return hours24
    }
    // 12-hour format
    return hours24 % 12 === 0 ? 12 : hours24 % 12
  }

  const getAmPm = (hours24: number): string => {
    return hours24 >= 12 ? "PM" : "AM"
  }

  const getTime = (): string => {
    let hours24: number
    let minutes: number
    let seconds: number

    if (timezone === "utc") {
      hours24 = date.getUTCHours()
      minutes = date.getUTCMinutes()
      seconds = date.getUTCSeconds()
    } else {
      hours24 = date.getHours()
      minutes = date.getMinutes()
      seconds = date.getSeconds()
    }

    const hours = getHours(hours24, timeFormat)
    const timeStr = showSeconds
      ? `${formatSegment(hours)}:${formatSegment(minutes)}:${formatSegment(seconds)}`
      : `${formatSegment(hours)}:${formatSegment(minutes)}`

    // Add AM/PM for 12-hour format
    if (timeFormat === "12h") {
      return `${timeStr} ${getAmPm(hours24)}`
    }

    return timeStr
  }

  const getTimezoneAbbr = (): string => {
    if (timezone === "utc") {
      return "UTC"
    }
    return getTimezoneAbbreviation(date)
  }

  const formatDate = (): string => {
    if (timezone === "utc") {
      return date.toLocaleDateString("en-NZ", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC"
      })
    }
    return date.toLocaleDateString("en-NZ", {
      year: "numeric",
      month: "long",
      day: "numeric"
    })
  }

  const formatTimeWithSeconds = (dateToFormat: Date, tz: "local" | "utc"): string => {
    let hours24: number
    let minutes: number
    let seconds: number

    if (tz === "utc") {
      hours24 = dateToFormat.getUTCHours()
      minutes = dateToFormat.getUTCMinutes()
      seconds = dateToFormat.getUTCSeconds()
    } else {
      hours24 = dateToFormat.getHours()
      minutes = dateToFormat.getMinutes()
      seconds = dateToFormat.getSeconds()
    }

    const hours = getHours(hours24, timeFormat)
    const timeStr = `${formatSegment(hours)}:${formatSegment(minutes)}:${formatSegment(seconds)}`

    // Add AM/PM for 12-hour format
    if (timeFormat === "12h") {
      return `${timeStr} ${getAmPm(hours24)}`
    }

    return timeStr
  }

  const formatFullDateTime = (dateToFormat: Date, tz: "local" | "utc"): string => {
    const dateStr = tz === "utc"
      ? dateToFormat.toLocaleDateString("en-NZ", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC"
      })
      : dateToFormat.toLocaleDateString("en-NZ", {
        year: "numeric",
        month: "long",
        day: "numeric"
      })
    const timeStr = formatTimeWithSeconds(dateToFormat, tz)
    return `${dateStr} ${timeStr}`
  }

  const sizeClasses = size === "large"
    ? "text-4xl md:text-6xl font-mono font-bold tabular-nums"
    : "text-sm md:text-base font-mono tabular-nums"

  const dateSizeClasses = size === "large"
    ? "text-lg md:text-xl font-mono tabular-nums"
    : "text-xs md:text-sm font-mono tabular-nums"

  const clockDisplay = (
    <div className={`inline-flex items-center gap-1.5 ${disableDialog ? "" : "cursor-pointer hover:opacity-80 transition-opacity"} ${className || ""}`}>
      {showDate && (
        <span className={`${dateSizeClasses} text-muted-foreground underline decoration-dotted underline-offset-2`}>
          {formatDate()}
        </span>
      )}
      <time
        dateTime={date.toISOString()}
        className={sizeClasses}
      >
        {getTime()}
      </time>
      {showTimezone && (
        <span className={`${sizeClasses} text-muted-foreground font-normal`}>
          {getTimezoneAbbr()}
        </span>
      )}
      {showLabel && !showTimezone && (
        <span className={`${sizeClasses} text-muted-foreground font-normal`}>
          {timezone.toUpperCase()}
        </span>
      )}
    </div>
  )

  if (disableDialog) {
    return clockDisplay
  }

  const dialogContent = (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Camera Clock Calibration</DialogTitle>
        <DialogDescription>
          Photograph this screen with your camera to calibrate the clock on your camera.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Local Time</p>
          <p className="text-2xl font-mono font-bold tabular-nums">
            {formatFullDateTime(date, "local")}
          </p>
          <p className="text-xs text-muted-foreground">
            {getTimezoneAbbreviation(date)}
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">UTC Time</p>
          <p className="text-2xl font-mono font-bold tabular-nums">
            {formatFullDateTime(date, "utc")}
          </p>
          <p className="text-xs text-muted-foreground">UTC</p>
        </div>
      </div>
    </DialogContent>
  )

  return (
    <Dialog>
      <DialogTrigger asChild>
        {clockDisplay}
      </DialogTrigger>
      {dialogContent}
    </Dialog>
  )
}
